import apiClient from "./client";
import type { ApiResponse } from "@/types";

const BASE = "/backup";

export interface COSObject {
  key: string;
  size: number;
  last_modified: string;
}

export interface StorageConfig {
  driver: string;
  endpoint: string;
  region: string;
  bucket: string;
  access_key: string;
  secret_key: string;
  use_ssl: boolean;
  path_prefix: string;
}

export interface BackupConfig {
  backup: {
    enabled: boolean;
    cron: string;
    retention_days: number;
    keep_local: boolean;
    local_dir: string;
    auto_upload_cos: boolean;
    cos_prefix: string;
    max_backups: number;
  };
  storage: StorageConfig;
}

export interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
}

export interface TestResult {
  success: boolean;
  error?: string;
}

export async function exportBackup(): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/export`, {
    responseType: "blob",
  });
  return data;
}

export interface BackupRecord {
  id: number;
  filename: string;
  size: number;
  storage_type: string;
  storage_key: string;
  status: string;
  error_msg?: string;
  created_at: string;
}

export async function getRecords(): Promise<ApiResponse<BackupRecord[]>> {
  const { data } = await apiClient.get(`${BASE}/records`);
  return data;
}

export async function triggerBackup(): Promise<ApiResponse<unknown>> {
  const { data } = await apiClient.post(`${BASE}/trigger`);
  return data;
}

export async function downloadCOS(key: string): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/download`, {
    params: { key },
    responseType: "blob",
  });
  return data;
}

export async function importBackup(
  file: File,
  mode: string,
): Promise<ApiResponse<{ result: ImportResult }>> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  const { data } = await apiClient.post(`${BASE}/import`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function syncBackupToCOS(): Promise<ApiResponse<{ key: string }>> {
  const { data } = await apiClient.post(`${BASE}/sync-cos`);
  return data;
}

export async function listCOSBackups(): Promise<ApiResponse<COSObject[]>> {
  const { data } = await apiClient.get(`${BASE}/cos-list`);
  return data;
}

export async function restoreFromCOS(
  key: string,
  mode: string,
): Promise<ApiResponse<{ result: ImportResult }>> {
  const { data } = await apiClient.post(`${BASE}/restore-cos`, { key, mode });
  return data;
}

export async function getBackupConfig(): Promise<ApiResponse<BackupConfig>> {
  const { data } = await apiClient.get(`${BASE}/config`);
  return data;
}

export async function updateBackupConfig(
  payload: BackupConfig,
): Promise<ApiResponse<unknown>> {
  const { data } = await apiClient.put(`${BASE}/config`, payload);
  return data;
}

export async function testCOSConnection(
  storage: StorageConfig,
): Promise<ApiResponse<TestResult>> {
  const { data } = await apiClient.post(`${BASE}/test-cos`, storage);
  return data;
}
